import type { LocationEntry } from '../../../../hooks/useGetLocations';
import type { OEMSettings, OrderingMarketplace } from '../../../../hooks/useGetSettings';
import { resolveLocationList, toLocationList } from '../../../../components/settings/locationListValue';

export interface OemOption {
    _id: string;
    name: string;
    similarMakes: Array<{ _id: string; name: string; }>;
}

/** Marketplaces that can qualify an order for an OEM rebate. CCC does not. */
export type RebateMarketplace = Exclude<OrderingMarketplace, 'ccc'>;

export const MARKETPLACE_OPTIONS: Array<RebateMarketplace> = [
    'opstrax',
    'collisionlink',
    'partstrader',
];

export const MARKETPLACE_LABELS: Record<OrderingMarketplace, string> = {
    ccc: 'CCC',
    opstrax: 'OPS',
    collisionlink: 'CollisionLink',
    partstrader: 'PartsTrader',
};

/** One rebate tier being edited. Text, so a half-typed value survives. */
export interface RebateTier {
    id: string;
    min: string;
    max: string;
    rebate: string;
}

export interface OemDraft {
    makeId: string;
    name: string;
    /** Other makes from the same OEM to save with these settings. */
    similarMakeIds: Array<string>;
    /** Certified location ids; saved as 'all' when every shop is picked. */
    locationIds: Array<string>;
    rebateEnabled: boolean;
    /** Marketplaces an order must go through to earn the rebate. */
    marketplaces: Array<RebateMarketplace>;
    tiers: Array<RebateTier>;
    overrideDrpThreshold: boolean;
}

let tierCounter = 0;
export const newRebateTier = (overrides?: Partial<RebateTier>): RebateTier => ({
    id: `tier-${++tierCounter}`,
    min: '',
    max: '',
    rebate: '',
    ...overrides,
});

const percentToText = (value: number): string => String(+(value * 100).toFixed(4));

export const buildOemDraft = (oem?: OEMSettings): OemDraft => ({
    makeId: oem?.makeId ?? '',
    name: oem?.name ?? '',
    similarMakeIds: [],
    locationIds: [],
    rebateEnabled: !!oem?.rebate,
    marketplaces: oem?.rebate?.requiredMarketplaces ?? [],
    tiers:
        oem?.rebate?.thressholds?.map((tier) =>
            newRebateTier({
                min: percentToText(tier.minThresshold),
                // The top tier is stored open-ended as 1; blank reads as "and up".
                max: tier.maxThresshold >= 1 ? '' : percentToText(tier.maxThresshold),
                rebate: percentToText(tier.rebate),
            }),
        ) ?? [],
    overrideDrpThreshold: oem?.rebate?.overrideDrpThresshold ?? false,
});

/**
 * Certified locations the make already holds, for seeding the picker. Stored
 * entries are resolved against the org's shops first, so a location that was
 * recreated still ticks its box.
 */
export const savedCertificationIds = (
    oem: OEMSettings | undefined,
    locations: LocationEntry[],
    allLocationIds: Array<string>,
): Array<string> => {
    if (!oem) return [];
    const resolved = resolveLocationList(oem.certifications, locations);
    return resolved === 'all' ? allLocationIds : resolved.map((location) => location._id);
};

/** A tier the user has actually begun filling in. */
const isTierStarted = (tier: RebateTier): boolean =>
    tier.min !== '' || tier.max !== '' || tier.rebate !== '';

/**
 * The settings shape for one make. Margin thresholds and the minimum quoting
 * time live under Ordering and Quoting now, so whatever is stored for the make
 * is carried through untouched rather than cleared.
 */
export const toOemPayload = (
    draft: OemDraft,
    makeId: string,
    name: string,
    locations: LocationEntry[],
    existing?: OEMSettings,
): OEMSettings => {
    const allSelected =
        locations.length > 0 &&
        locations.every((location) => draft.locationIds.includes(location._id as string));

    return {
        makeId,
        name,
        certifications: allSelected ? 'all' : toLocationList(draft.locationIds, locations),
        drpThreshold: existing?.drpThreshold ?? null,
        nonDrpThreshold: existing?.nonDrpThreshold ?? null,
        minQuotingTime: existing?.minQuotingTime ?? null,
        rebate: draft.rebateEnabled
            ? {
                requiredMarketplaces: draft.marketplaces,
                thressholds: draft.tiers.filter(isTierStarted).map((tier) => ({
                    minThresshold: (Number(tier.min) || 0) / 100,
                    // Blank max is the open-ended top tier.
                    maxThresshold: tier.max === '' ? 1 : (Number(tier.max) || 0) / 100,
                    rebate: (Number(tier.rebate) || 0) / 100,
                })),
                overrideDrpThresshold: draft.overrideDrpThreshold,
            }
            : undefined,
    };
};

/** "30% - 60%", "85%+", for the tier's plain-language summary. */
export const formatTierRange = (tier: RebateTier): string => {
    if (!tier.min && !tier.max) return 'New tier';
    if (tier.min && !tier.max) return `${tier.min}%+`;
    if (!tier.min && tier.max) return `0% - ${tier.max}%`;
    return `${tier.min}% - ${tier.max}%`;
};
